// Wordmark — renders the quit-kratom mark + "quit kratom" in Fraunces.
// Inlined SVG so we never wait on a network roundtrip for the brand mark.
//
// The aubergine bars use `currentColor` so dark mode swaps automatically
// via the wrapping `color` style. The copper accent bar keeps its
// hardcoded value (it's the same warm tone in both themes by design).

interface WordmarkProps {
  variant?: 'lockup' | 'stacked' | 'mark-only';
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const SIZE_PX: Record<NonNullable<WordmarkProps['size']>, number> = {
  small: 28,
  medium: 44,
  large: 72,
};

export function Wordmark({
  variant = 'lockup',
  size = 'medium',
  className,
}: WordmarkProps) {
  const px = SIZE_PX[size];

  if (variant === 'mark-only') {
    return (
      <svg
        viewBox="0 0 64 64"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="quit kratom"
        className={className}
        style={{
          inlineSize: px,
          blockSize: px,
          color: 'var(--color-primary)',
        }}
      >
        <title>quit kratom</title>
        <rect x="8" y="20" width="48" height="4" rx="2" fill="currentColor" />
        <rect x="8" y="30" width="34" height="4" rx="2" fill="currentColor" />
        <rect x="8" y="40" width="18" height="4" rx="2" fill="var(--color-accent)" />
      </svg>
    );
  }

  if (variant === 'stacked') {
    return (
      <svg
        viewBox="0 0 192 128"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="quit kratom"
        className={className}
        style={{
          inlineSize: px * (192 / 128),
          blockSize: px,
          color: 'var(--color-primary)',
        }}
      >
        <title>quit kratom</title>
        <g transform="translate(64 8)">
          <rect x="8" y="20" width="48" height="4" rx="2" fill="currentColor" />
          <rect x="8" y="30" width="34" height="4" rx="2" fill="currentColor" />
          <rect x="8" y="40" width="18" height="4" rx="2" fill="var(--color-accent)" />
        </g>
        <text
          x="96"
          y="106"
          textAnchor="middle"
          fontFamily="'Fraunces', ui-serif, Georgia, serif"
          fontSize="28"
          fontWeight={500}
          letterSpacing="-0.28"
          fill="var(--color-ink-primary)"
        >
          quit kratom
        </text>
      </svg>
    );
  }

  // lockup (default)
  return (
    <svg
      viewBox="0 0 256 64"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="quit kratom"
      className={className}
      style={{
        inlineSize: px * (256 / 64),
        blockSize: px,
        color: 'var(--color-primary)',
      }}
    >
      <title>quit kratom</title>
      <g>
        <rect x="8" y="20" width="48" height="4" rx="2" fill="currentColor" />
        <rect x="8" y="30" width="34" height="4" rx="2" fill="currentColor" />
        <rect x="8" y="40" width="18" height="4" rx="2" fill="var(--color-accent)" />
      </g>
      <text
        x="76"
        y="42"
        fontFamily="'Fraunces', ui-serif, Georgia, serif"
        fontSize="32"
        fontWeight={500}
        letterSpacing="-0.32"
        fill="var(--color-ink-primary)"
      >
        quit kratom
      </text>
    </svg>
  );
}
